import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useSnapshot } from "valtio";
import { appStore } from "@store/appStore";
import { PermissionLevels } from "@models/permission.model";
import { IngestDefinitionService } from "@services/ingest/ingestDefinitionService.ts";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import IngestDefinitionDialog from "./IngestDefinitionDialog";

interface IngestDefinition {
  id: number;
  name: string;
  is_active: boolean;
  organization: { id: number; name: string } | null;
  file_key: string | null;
  population_segment_name: string | null;
}

export default function IngestListPage() {
  useDocumentTitle("Ingest Configs");
  const [configs, setConfigs] = useState<IngestDefinition[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  // null → dialog is in create mode; a number → editing that definition.
  const [editingId, setEditingId] = useState<number | null>(null);
  const navigate = useNavigate();
  const appSnap = useSnapshot(appStore);

  // Creating/editing a definition requires config-authoring rights; the list
  // itself is also visible to upload-only roles, who must not see those actions.
  const canEdit =
    appSnap.loggedInUser?.permissions?.hasAccess?.("Config List", PermissionLevels.CREATE) ?? false;

  const loadConfigs = () => {
    new IngestDefinitionService().fetchDefinitions().then(setConfigs);
  };

  useEffect(() => {
    loadConfigs();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setDialogOpen(true);
  };

  const openEdit = (id: number) => {
    setEditingId(id);
    setDialogOpen(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Ingest Definitions</Typography>
        {canEdit && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{ textTransform: "none", bgcolor: "#845adf", "&:hover": { bgcolor: "#6e44c8" } }}
          >
            New Ingest Definition
          </Button>
        )}
      </Box>

      <IngestDefinitionDialog
        open={dialogOpen}
        definitionId={editingId}
        onClose={() => setDialogOpen(false)}
        onSaved={(id) => {
          setDialogOpen(false);
          if (editingId == null) {
            // New definition → jump straight into authoring its field map.
            navigate(`/configs/${id}/field-map`);
          } else {
            loadConfigs();
          }
        }}
      />

      <TableContainer sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #f3f3f3" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#f9fafb" }}>
              <TableCell sx={{ fontWeight: 600, color: "#8c9097", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.3 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#8c9097", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.3 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#8c9097", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.3 }}>Organization</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#8c9097", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.3 }}>File Key</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#8c9097", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.3 }}>Population Segment</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#8c9097", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.3 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {configs.map((cfg) => (
              <TableRow key={cfg.id} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                <TableCell sx={{ fontWeight: 500 }}>{cfg.name}</TableCell>
                <TableCell>
                  <span style={{ color: cfg.is_active ? "#26bf94" : "#8c9097", fontSize: 13 }}>
                    {cfg.is_active ? "Active" : "Inactive"}
                  </span>
                </TableCell>
                <TableCell>{cfg.organization?.name ?? "—"}</TableCell>
                <TableCell sx={{ color: "#555", fontSize: 13, fontFamily: "monospace" }}>{cfg.file_key ?? "—"}</TableCell>
                <TableCell>{cfg.population_segment_name ?? "—"}</TableCell>
                <TableCell align="right">
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                    {canEdit && (
                      <Box
                        component="a"
                        onClick={() => openEdit(cfg.id)}
                        sx={{
                          color: "#845adf",
                          fontSize: 13,
                          fontWeight: 500,
                          cursor: "pointer",
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        Edit
                      </Box>
                    )}
                    <Box
                      component="a"
                      onClick={() => navigate(`/configs/${cfg.id}/field-map`)}
                      sx={{
                        color: "#845adf",
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: "pointer",
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      Field Map
                    </Box>
                    <Box
                      component="a"
                      onClick={() => navigate(`/configs/${cfg.id}/files`)}
                      sx={{
                        color: "#845adf",
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: "pointer",
                        textDecoration: "none",
                        display: "inline-flex",
                        alignItems: "center",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      Uploaded Files
                      <ChevronRightIcon sx={{ fontSize: 18, ml: "-2px" }} />
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
